import { type MonthGuidance, useGuidance } from "@/hooks/use-guidance";

export type GuidanceTier = "low" | "moderate" | "high" | "none";

/** Pure: tier from guidance/capacity ratio. `none` = unknown (render muted, never green/zero). */
export function guidanceTier(
  guidanceHours: number | null,
  capacityHours: number | null,
): GuidanceTier {
  if (guidanceHours === null) return "none";
  if (capacityHours === null || capacityHours <= 0) return "none";
  const ratio = guidanceHours / capacityHours;
  if (ratio <= 0.7) return "low";
  if (ratio <= 1.0) return "moderate";
  return "high";
}

/** Pure: free capacity as a 0–100 percent. null = unknown. Overload clamps to 0 (never negative). */
export function availabilityPercent(
  guidanceHours: number | null,
  capacityHours: number | null,
): number | null {
  if (guidanceHours === null) return null;
  if (capacityHours === null || capacityHours <= 0) return null;
  const pct = ((capacityHours - guidanceHours) / capacityHours) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

// Reuse the existing fit-tier CSS vars — do not invent a new color scale (WI-002 AC).
const TIER_CLASS: Record<GuidanceTier, string> = {
  low: "bg-[var(--fit-strong-bg)] text-[var(--fit-strong)]",
  moderate: "bg-[var(--fit-moderate-bg)] text-[var(--fit-moderate)]",
  high: "bg-[var(--fit-at-risk-bg)] text-[var(--fit-at-risk)]",
  none: "bg-muted text-muted-foreground",
};

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Subtle: month label + tier color only. Full detail (hours / capacity /
// availability) stays available on hover via the title tooltip — keeps the
// assignment row low-cognitive-load so the Fit Score can dominate.
function Cell({ m }: { m: MonthGuidance }) {
  const tier = guidanceTier(m.guidanceHours, m.capacityHours);
  const avail = availabilityPercent(m.guidanceHours, m.capacityHours);
  const label = MONTH_ABBR[m.month - 1] ?? "";
  return (
    <div
      className={`flex min-w-[26px] flex-1 items-center justify-center rounded px-1 py-1 ${TIER_CLASS[tier]} ${
        m.isCurrent ? "ring-1 ring-brand-500" : ""
      }`}
      title={`${label} ${m.year}${m.isCurrent ? " (current month)" : ""} · guidance ${m.guidanceHours ?? "—"}h / capacity ${m.capacityHours ?? "—"}h · ${avail === null ? "availability unknown" : `${avail}% free`}`}
    >
      <span className="text-[9px] font-semibold uppercase leading-none tracking-wide opacity-80">
        {label}
      </span>
    </div>
  );
}

export function GuidanceStrip({ resourceId }: { resourceId: string }) {
  const { byResource, isLoading } = useGuidance();
  if (isLoading) return null;
  const months = byResource.get(resourceId);
  if (!months || months.length === 0) return null;
  return (
    <div className="mt-1 flex gap-0.5">
      {months.slice(0, 4).map((m) => (
        <Cell key={m.monthStart} m={m} />
      ))}
    </div>
  );
}
